/**
 * useWidgetDefinitions — hooki katalogu definicji widgetów danych z kreatora (Faza 23).
 *
 * Kontrakt backendu (patrz `apps/api/app/api/widget-definitions/`):
 *  - GET    /widget-definitions[?include_deleted=true]  → { data: WidgetDefinitionDTO[] }
 *  - POST   /widget-definitions  → 201 { data }  (409 NAME_TAKEN, 400 INVALID_SOURCE, 422)
 *  - PATCH  /widget-definitions/:id  → 200 { data }
 *  - DELETE /widget-definitions/:id[?hard=true&reason=]  → 200 { ok, hard }
 *  - POST   /widget-definitions/:id/restore  → 200 { ok }
 *  - POST   /widget-definitions/preview  → 200 WidgetComputeResult  (podgląd SZKICU w kreatorze)
 *  - POST   /widget-definitions/:id/data[?recordId=&entity=]  → 200 WidgetDataApiResponse
 *    (WidgetComputeResult + visualType definicji)
 *
 * CRUD katalogu bramkuje `crm_core.widgets.manage` (encja tenant-globalna). `X-Company-Id`
 * dopina globalny interceptor (`lib/api.ts`) z aktywnej firmy top-baru — świadomie nie wymuszamy
 * tu jawnego headera: pulpit i podgląd liczą w zakresie wybranej firmy (albo unii, gdy aktywne
 * „Wszystkie firmy"), spójnie z resztą pulpitu.
 */

import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import type {
  WidgetComputeResult,
  WidgetDataApiResponse,
  WidgetDefinitionConfig,
  WidgetDefinitionDTO,
  WidgetSourceMeta,
  WidgetVisualType,
} from '@/lib/widgets/types';

// ─── Query keys ───────────────────────────────────────────────────────────────

const WIDGET_DEFINITIONS_KEY = ['widget-definitions'] as const;

export function widgetDefinitionsKey(includeDeleted: boolean) {
  return [...WIDGET_DEFINITIONS_KEY, includeDeleted] as const;
}

export function widgetDataKey(
  definitionId: string,
  recordId: string | null | undefined,
  entity: string | null | undefined,
  companyScope: string | null | undefined,
) {
  return [
    'widget-data',
    definitionId,
    recordId ?? null,
    entity ?? null,
    companyScope ?? null,
  ] as const;
}

// ─── Input types ───────────────────────────────────────────────────────────────

export interface WidgetDefinitionInputBase {
  name: string;
  description?: string | null;
  icon?: string | null;
  visualType: WidgetVisualType;
  source: string;
  config: WidgetDefinitionConfig;
  compatibleEntities?: string[];
  isActive?: boolean;
}

export type CreateWidgetDefinitionInput = WidgetDefinitionInputBase;
export type UpdateWidgetDefinitionInput = Partial<WidgetDefinitionInputBase>;

/** Ciało podglądu = sam spec (bez pól katalogowych) — nic się nie zapisuje. */
export interface PreviewWidgetInput {
  visualType: WidgetVisualType;
  source: string;
  config: WidgetDefinitionConfig;
}

/** Kontekst rekordu (host formularza) — idzie w QUERY, nigdy w body. */
export interface WidgetDataContext {
  recordId?: string;
  entity?: string;
}

// ─── Queries ────────────────────────────────────────────────────────────────

/**
 * Metadane źródeł dla kreatora (pola z typem, pola-powiązania, domyślne pole daty, encje
 * kontekstu). Statyczne w skali sesji — długi `staleTime`.
 */
export function useWidgetSources(options?: { enabled?: boolean }) {
  return useQuery<WidgetSourceMeta[]>({
    queryKey: ['widget-sources'],
    queryFn: async () => {
      const res = await api.get<{ data: WidgetSourceMeta[] }>('/widget-definitions/sources');
      return res.data.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

/** Lista definicji (posortowana po nazwie server-side). `includeDeleted` pokazuje też skasowane. */
export function useWidgetDefinitions(includeDeleted = false, options?: { enabled?: boolean }) {
  return useQuery<WidgetDefinitionDTO[]>({
    queryKey: widgetDefinitionsKey(includeDeleted),
    queryFn: async () => {
      const res = await api.get<{ data: WidgetDefinitionDTO[] }>('/widget-definitions', {
        params: includeDeleted ? { include_deleted: 'true' } : undefined,
      });
      return res.data.data;
    },
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}

/**
 * Dane JEDNEGO widgetu z zapisanej definicji (host pulpitu / formularza). Wynik
 * `WidgetDataApiResponse` (`WidgetComputeResult` + `visualType`) przychodzi w treści 200 — kafel
 * degraduje się sam, dlatego traktujemy błąd HTTP (401/403/404/500) i `ok:false` osobno w
 * komponencie kafla.
 */
export function useWidgetData(
  definitionId: string,
  context?: WidgetDataContext,
  options?: { enabled?: boolean; companyScope?: string | null },
) {
  const recordId = context?.recordId ?? null;
  const entity = context?.entity ?? null;
  // `companyScope` w kluczu (np. id aktywnej firmy albo 'all') — przełączenie firmy w top-barze
  // musi odświeżyć kafel; `X-Company-Id` dopina interceptor, więc scope jest tylko cache-busterem.
  const companyScope = options?.companyScope ?? null;
  return useQuery<WidgetDataApiResponse>({
    queryKey: widgetDataKey(definitionId, recordId, entity, companyScope),
    queryFn: async () => {
      const res = await api.post<WidgetDataApiResponse>(
        `/widget-definitions/${definitionId}/data`,
        undefined,
        { params: recordId && entity ? { recordId, entity } : undefined },
      );
      return res.data;
    },
    enabled: (options?.enabled ?? true) && !!definitionId,
    staleTime: 30_000,
    retry: false,
  });
}

// ─── Error mapping (kod błędu API → klucz i18n) ────────────────────────────────

function resolveWidgetErrorKey(code: string | undefined): string {
  switch (code) {
    case 'NAME_TAKEN':
      return 'tenant.widgets.errors.nameTaken';
    case 'INVALID_SOURCE':
      return 'tenant.widgets.errors.invalidSource';
    case 'VALIDATION_ERROR':
      return 'tenant.widgets.errors.validation';
    case 'INVALID_CONFIG':
    case 'INVALID_DEFINITION_CONFIG':
      return 'tenant.widgets.errors.invalidConfig';
    case 'NOT_FOUND':
    case 'WIDGET_DEFINITION_NOT_FOUND':
      return 'tenant.widgets.errors.notFound';
    case 'ALREADY_DELETED':
      return 'tenant.widgets.errors.alreadyDeleted';
    case 'NOT_DELETED':
      return 'tenant.widgets.errors.notDeleted';
    case 'PERMISSION_DENIED':
      return 'tenant.widgets.errors.permissionDenied';
    default:
      return 'tenant.widgets.errors.generic';
  }
}

/** Eksport na potrzeby błędów inline w formularzu kreatora (poza toastem). */
export { resolveWidgetErrorKey };

// ─── Mutations: CRUD katalogu ──────────────────────────────────────────────────

/**
 * Mutacje zarządzania katalogiem definicji. Toast sukces/błąd wbudowany (kod błędu API mapowany
 * przez `resolveWidgetErrorKey`); `deleteDefinition` po miękkim skasowaniu oferuje „Cofnij"
 * (woła `restoreDefinition`). Invalidacja z PREFIKSEM `['widget-definitions']` (React Query v5
 * partial match) odświeża oba warianty listy (`includeDeleted` true/false) jednym wywołaniem.
 */
export function useWidgetDefinitionMutations() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation();

  function invalidateAll() {
    return queryClient.invalidateQueries({ queryKey: WIDGET_DEFINITIONS_KEY });
  }

  function toastError(err: unknown) {
    toast.show({ variant: 'error', title: t(resolveWidgetErrorKey(extractErrorCode(err))) });
  }

  const createDefinition = useMutation({
    mutationFn: async (input: CreateWidgetDefinitionInput) => {
      const res = await api.post<{ data: WidgetDefinitionDTO }>('/widget-definitions', input);
      return res.data.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.widgets.toast.createSuccess') });
      void invalidateAll();
    },
    onError: toastError,
  });

  const updateDefinition = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UpdateWidgetDefinitionInput }) => {
      const res = await api.patch<{ data: WidgetDefinitionDTO }>(
        `/widget-definitions/${id}`,
        values,
      );
      return res.data.data;
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.widgets.toast.updateSuccess') });
      void invalidateAll();
    },
    onError: toastError,
  });

  const restoreDefinition = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/widget-definitions/${id}/restore`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.widgets.toast.restoreSuccess') });
      void invalidateAll();
    },
    onError: toastError,
  });

  const deleteDefinition = useMutation({
    mutationFn: async ({ id, hard, reason }: { id: string; hard?: boolean; reason?: string }) => {
      const params: Record<string, string> = {};
      if (hard) params.hard = 'true';
      if (reason) params.reason = reason;
      await api.delete(`/widget-definitions/${id}`, {
        params: Object.keys(params).length ? params : undefined,
      });
      return { id, hard: !!hard };
    },
    onSuccess: ({ id, hard }) => {
      // Twarde usunięcie jest nieodwracalne — bez akcji „Cofnij". Miękkie oferuje przywrócenie.
      toast.show({
        variant: 'success',
        title: t(
          hard ? 'tenant.widgets.toast.hardDeleteSuccess' : 'tenant.widgets.toast.deleteSuccess',
        ),
        duration: hard ? undefined : 8000,
        action: hard
          ? undefined
          : {
              label: t('tenant.widgets.undoDelete'),
              onClick: () => {
                restoreDefinition.mutate(id);
              },
            },
      });
      void invalidateAll();
    },
    onError: toastError,
  });

  return { createDefinition, updateDefinition, deleteDefinition, restoreDefinition };
}

// ─── Mutation: podgląd szkicu (kreator) ─────────────────────────────────────────

/**
 * Podgląd na żywo z bieżącego szkicu (krok „Podgląd" kreatora) — POST /widget-definitions/preview.
 * BEZ toasta: wynik (ok albo failure) renderuje panel podglądu inline. Zwraca `WidgetComputeResult`
 * z treści 200; błąd HTTP (403/422/500) łapie caller przez `mutateAsync().catch`.
 */
export function usePreviewWidget() {
  return useMutation<WidgetComputeResult, unknown, PreviewWidgetInput>({
    mutationFn: async (input: PreviewWidgetInput) => {
      const res = await api.post<WidgetComputeResult>('/widget-definitions/preview', input);
      return res.data;
    },
  });
}
